import Asset, { AssetData } from './asset';
import type { AssetTypes, UxAsset } from './index';
import { lowestNumber } from '../util/parse-data';
import { FilterGroup } from '../typings';
import VehicleType from '../enums/vehicle-type';

type FilterMapFn = (filterGroup: FilterGroup) => boolean;
type FilterMap = Map<string, FilterMapFn>;

export default abstract class GenericAsset<T extends AssetData> extends Asset<T> {
  protected vehicleType: VehicleType;

  constructor(type: keyof AssetTypes, vehicleType: VehicleType, data: T) {
    super(type, data);
    this.vehicleType = vehicleType;
  }

  get prices(): number[] {
    const ret = [];
    if (this.price) ret.push(this.price);
    if (this.originalPrice) ret.push(this.originalPrice);
    return ret;
  }

  /**
   *
   * Function to format strings for comparison
   */
  formatString(key?: any): any {
    if (typeof key === 'string') {
      return key.toLowerCase().replace(/\s/g, '');
    }
    return key;
  }

  /**
   * Functionality to filter the CTA based on the filter groups provided and
   * the asset data.
   * Returns true if the CTA should be filtered out
   * Returns false if the CTA should be shown
   * @param filterGroups - Array of filter groups to used as filters
   * @returns boolean - true if the CTA should be filtered out, false otherwise
   */
  filterCTA(filterGroups: FilterGroup[]): boolean {
    const lowPrice: number = lowestNumber(...this.prices);
    const filterMap: FilterMap = new Map<string, FilterMapFn>();
    const uxAsset = this.toUxServer();

    //build map for special price comparisons
    filterMap.set(
      'product_price_less_than',
      (filterGroup) => lowPrice < Number(filterGroup['product_price_less_than'])
    );
    filterMap.set(
      'product_price_greater_than',
      (filterGroup) => lowPrice > Number(filterGroup['product_price_greater_than'])
    );
    return this.evalFilterGroups(filterGroups, filterMap, uxAsset);
  }

  private evalFilterGroups(filterGroups: FilterGroup[], filterMap: FilterMap, uxAsset: UxAsset): boolean {
    for (const group of filterGroups) {
      let filtered = true;
      for (const key in group) {
        if (filterMap.has(key)) {
          const filter = filterMap.get(key)!;
          filtered = filtered && filter(group);
          //generic comparisons
        } else if (this.formatString(uxAsset[key as keyof UxAsset]) !== this.formatString(group[key])) {
          filtered = false;
        }
      }
      if (filtered) return true;
    }
    return false;
  }

  /**
   * Convert a PowersportsAsset to the fields the UX server expects
   */
  toUxServer(): UxAsset {
    return {
      product_id: null,
      product_vin: null,
      product_stock_number: null,
      product_category: null,
      product_subcategory: null,
      product_make: null,
      product_model: null,
      product_year: null,
      product_color: null,
      product_condition: null,
      product_price: this.price,
      product_original_price: this.originalPrice,
      product_on_sale: false,
      product_location: null,
      product_origin_url: this.origin_url.toString(),
      product_dealer_id: null,
      product_ad_id: null,
      product_vehicle_type: this.vehicleType,
      cta_type: null
    };
  }
}
